import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { VibeAuditCapacitySubjectReadSchema } from "./read-vocabularies.schema";

/**
 * Emitted when a capacity approval request is RAISED — normally by the agent's
 * disk-pressure detector reporting a subject over its threshold, which the
 * control plane folds into a PENDING request. Persisted INSIDE the same
 * `$transaction` as the request INSERT, so a raised request always has its audit
 * row (and a request that lost the one-PENDING-per-(org,subject) race leaves
 * neither).
 *
 * Captures the pressure that justified the request, so the audit feed answers
 * "why was this grow proposed?" from the row alone — the node stats behind it are
 * transient and are never persisted anywhere else.
 */
export const VibeAuditCapacityRequestedPayloadSchema = z.object({
  eventType: z.literal("CAPACITY_REQUESTED"),
  /// The request this raised. UUID join key back to the
  /// VibeCapacityApprovalRequest row.
  vibeCapacityApprovalRequestId: z.string().uuid(),
  /// Which node-size knob the grow targets. Derived from the generated enum
  /// (no hardcoded literals).
  subject: z.enum(DbEnum.VIBE_CAPACITY_APPROVAL_SUBJECT_VALUES),
  /// The subject's root size (GiB) at raise time, and the SUGGESTED target. The
  /// approver may override the target when deciding, so this records what was
  /// proposed — the approved value lives on the CAPACITY_APPROVED row.
  currentGb: z.number().int().positive(),
  targetGb: z.number().int().positive(),
  /// The tightest node's root utilisation (%) and the bar it crossed, as the
  /// agent reported them. Preserved here because the underlying node stats are
  /// never persisted — without this the audit feed could not explain the raise.
  usedPercent: z.number().min(0).max(100),
  thresholdPercent: z.number().min(0).max(100),
  /// Who requested the grow: null for the system threshold detector (the common
  /// path), set when a user requested it manually.
  requestedByUserId: z.string().min(1).nullable()
});
export type VibeAuditCapacityRequestedPayload = z.infer<
  typeof VibeAuditCapacityRequestedPayloadSchema
>;

const VibeAuditCapacityDecisionShape = {
  /// The capacity approval request this decision resolved. UUID join key
  /// back to the VibeCapacityApprovalRequest row.
  vibeCapacityApprovalRequestId: z.string().uuid(),
  /// Which node-size knob the grow targeted. Derived from the generated
  /// enum (no hardcoded literals).
  subject: z.enum(DbEnum.VIBE_CAPACITY_APPROVAL_SUBJECT_VALUES),
  /// Root size (GiB) at request time and the APPROVED target — captured so
  /// the audit feed answers "how big a grow was approved?" from the row
  /// alone, without re-reading the (possibly since-superseded) request.
  /// `targetGb` is the EFFECTIVE approved size: the approver's override when
  /// they supplied one, else the request's suggestion. The originally suggested
  /// value stays recoverable from the request's CAPACITY_REQUESTED row.
  currentGb: z.number().int().positive(),
  targetGb: z.number().int().positive(),
  /// The reviewer who decided. Non-null at decision time (the endpoint
  /// requires an authenticated actor); the payload snapshot preserves the
  /// identity in JSONB even after the request's FK column is SET NULL on a
  /// user delete.
  deciderUserId: z.string().min(1),
  /// True iff this decision crossed the request's `requiredApprovals`
  /// threshold (APPROVED) or was a reject (REJECTED). In-flight approves
  /// that don't yet decide emit no audit row — the field exists so a future
  /// "every decision" event type stays additive.
  decisive: z.boolean(),
  /// Optional reviewer note (the customer-visible reason on a reject).
  note: z.string().nullable()
};

/**
 * Emitted when a reviewer decision moves a capacity approval request to
 * APPROVED. Persisted INSIDE the same `$transaction` as the decision insert +
 * status-guarded request flip + the target-size write onto VibeTenantCluster,
 * so the audit row + the approved grow land atomically. The provisioner
 * converge then stamps the approved size on its next run.
 */
export const VibeAuditCapacityApprovedPayloadSchema = z.object({
  eventType: z.literal("CAPACITY_APPROVED"),
  ...VibeAuditCapacityDecisionShape
});
export type VibeAuditCapacityApprovedPayload = z.infer<
  typeof VibeAuditCapacityApprovedPayloadSchema
>;

/**
 * Emitted when a reviewer decision moves a capacity approval request to
 * REJECTED. No size is written and no converge is triggered — the request
 * closes and the audit row records the reject + the reviewer's note.
 */
export const VibeAuditCapacityRejectedPayloadSchema = z.object({
  eventType: z.literal("CAPACITY_REJECTED"),
  ...VibeAuditCapacityDecisionShape
});
export type VibeAuditCapacityRejectedPayload = z.infer<
  typeof VibeAuditCapacityRejectedPayloadSchema
>;

/**
 * Emitted when the approval expiry sweep moves a capacity approval request that
 * nobody decided from PENDING to EXPIRED. Persisted INSIDE the same
 * `$transaction` as the status-guarded flip, so an expired request always has its
 * row and a request a reviewer decided first has neither. No size is written and
 * no converge is triggered; the flip frees the one-PENDING-per-(org, subject)
 * slot, so the agent's next over-threshold report can raise a fresh request.
 * `actorUserId` is null — the sweep is a system actor.
 */
export const VibeAuditCapacityExpiredPayloadSchema = z.object({
  eventType: z.literal("CAPACITY_EXPIRED"),
  /// The request that expired. UUID join key back to the
  /// VibeCapacityApprovalRequest row.
  vibeCapacityApprovalRequestId: z.string().uuid(),
  /// Which node-size knob the unanswered grow targeted.
  subject: z.enum(DbEnum.VIBE_CAPACITY_APPROVAL_SUBJECT_VALUES),
  /// The root size (GiB) at raise time and the suggested target nobody decided.
  currentGb: z.number().int().positive(),
  targetGb: z.number().int().positive()
});
export type VibeAuditCapacityExpiredPayload = z.infer<typeof VibeAuditCapacityExpiredPayloadSchema>;

/**
 * Emitted when a tenant-cluster converge COMPLETES carrying an approved capacity
 * target: every stack applied with the approved root size, so the node group's
 * launch configuration now carries it. The node roll that moves the running
 * nodes onto the bigger root follows, and the cluster reads UPDATING until it
 * settles. Written at most once per request; `actorUserId` is null (system
 * actor).
 */
export const VibeAuditCapacityGrownPayloadSchema = z.object({
  eventType: z.literal("CAPACITY_GROWN"),
  /// The APPROVED request whose target this converge applied. UUID join key
  /// back to the VibeCapacityApprovalRequest row.
  vibeCapacityApprovalRequestId: z.string().uuid(),
  /// Which node-size knob grew.
  subject: z.enum(DbEnum.VIBE_CAPACITY_APPROVAL_SUBJECT_VALUES),
  /// The root size (GiB) the request was raised against, and the approved size
  /// the converge applied.
  currentGb: z.number().int().positive(),
  targetGb: z.number().int().positive()
});
export type VibeAuditCapacityGrownPayload = z.infer<typeof VibeAuditCapacityGrownPayloadSchema>;

/**
 * The READ side of this family's branches: what the audit feed's readers parse.
 * Each one widens only its enum fields, to the lenient read vocabularies in
 * `read-vocabularies.schema.ts` — a value a newer backend writes reaches an
 * older reader as that field's unlisted value instead of failing the feed page.
 * The emitting branches above stay strict.
 */
const VibeAuditCapacitySubjectRead = { subject: VibeAuditCapacitySubjectReadSchema };

export const VibeAuditCapacityRequestedReadPayloadSchema =
  VibeAuditCapacityRequestedPayloadSchema.extend(VibeAuditCapacitySubjectRead);
export const VibeAuditCapacityApprovedReadPayloadSchema =
  VibeAuditCapacityApprovedPayloadSchema.extend(VibeAuditCapacitySubjectRead);
export const VibeAuditCapacityRejectedReadPayloadSchema =
  VibeAuditCapacityRejectedPayloadSchema.extend(VibeAuditCapacitySubjectRead);
export const VibeAuditCapacityExpiredReadPayloadSchema =
  VibeAuditCapacityExpiredPayloadSchema.extend(VibeAuditCapacitySubjectRead);
export const VibeAuditCapacityGrownReadPayloadSchema = VibeAuditCapacityGrownPayloadSchema.extend(
  VibeAuditCapacitySubjectRead
);
